import { useCallback, useEffect, useState } from "react";
import { useRoute, Link } from "wouter";
import { Lock, ShieldAlert, CheckCircle2, FileSignature, Loader2, Languages } from "lucide-react";
import { toast } from "sonner";
import { ArbiterShell } from "@/components/ArbiterShell";
import { getApiErrorDetail } from "@/lib/api";
import * as delib from "@/lib/deliberationApi";
import { useDeliberationI18n, type Lang } from "@/lib/deliberationI18n";

const GOLD = "oklch(0.695 0.105 68)";
const GRAPHITE = "oklch(0.18 0.02 243)";
const BORDER = "color-mix(in oklch, oklch(0.695 0.105 68) 18%, transparent)";

type T = (k: string) => string;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-md border p-4 space-y-3" style={{ borderColor: BORDER }}>
      <div className="text-sm font-semibold" style={{ color: GOLD }}>{title}</div>
      {children}
    </section>
  );
}

function SignaturePanel({ dossierId, versionId, t }: { dossierId: number; versionId: string; t: T }) {
  const [resp, setResp] = useState(false);
  const [mfa, setMfa] = useState(false);
  const [status, setStatus] = useState<delib.SignaturesStatus | null>(null);

  const reload = useCallback(() => {
    delib.getSignatures(dossierId, versionId).then(setStatus).catch(() => {});
  }, [dossierId, versionId]);
  useEffect(reload, [reload]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast.success(ok); reload(); }
    catch (e) { toast.error(getApiErrorDetail(e) ?? "Action refusée."); }
  };

  return (
    <div className="mt-2 rounded-md border p-2 space-y-2 text-[13px]" style={{ borderColor: BORDER }}>
      <div className="flex flex-wrap items-center gap-2">
        <FileSignature size={13} style={{ color: GOLD }} /> Bouton Rouge
        {status && (
          <span className="ml-auto text-[11px]" style={{ opacity: 0.75 }}>
            {status.confirmees}/{status.requis} {status.signee ? `· ${t("signed")} ✓` : ""}
          </span>
        )}
      </div>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={resp} onChange={(e) => setResp(e.target.checked)} />
        {t("br_resp")}
      </label>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={mfa} onChange={(e) => setMfa(e.target.checked)} />
        {t("br_mfa")}
      </label>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="rounded-md border px-2 py-1" style={{ borderColor: BORDER, color: GOLD }}
          onClick={() => act(() => delib.signArm(dossierId, versionId, { responsibility_accepted: resp, mfa_verified: mfa }), "OK")}>
          {t("br_arm")}
        </button>
        <button type="button" className="rounded-md px-2 py-1 font-medium" style={{ background: GOLD, color: GRAPHITE }}
          onClick={() => act(() => delib.signConfirm(dossierId, versionId), "OK")}>
          {t("br_confirm")}
        </button>
        <button type="button" className="rounded-md border px-2 py-1" style={{ borderColor: BORDER }}
          onClick={() => act(() => delib.signCancel(dossierId, versionId), "OK")}>
          {t("br_cancel")}
        </button>
      </div>
    </div>
  );
}

export default function DeliberationWorkspace() {
  const [, params] = useRoute<{ caseId: string }>("/cases/:caseId/deliberation");
  const dossierId = params?.caseId ? Number.parseInt(params.caseId, 10) : NaN;
  const { lang, setLang, t } = useDeliberationI18n();

  const [lock, setLock] = useState<delib.LockStatus | null>(null);
  const [sessions, setSessions] = useState<delib.DeliberationSession[]>([]);
  const [conc, setConc] = useState<delib.ConcordanceProgress | null>(null);
  const [notes, setNotes] = useState<delib.Note[]>([]);
  const [versions, setVersions] = useState<delib.SentenceVersion[]>([]);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chef, setChef] = useState("");
  const [noteTxt, setNoteTxt] = useState("");
  const [dispositif, setDispositif] = useState("");
  const [consent, setConsent] = useState<boolean | null>(null);
  const [votes, setVotes] = useState<delib.Vote[]>([]);
  const [voteType, setVoteType] = useState("MAJORITY");
  const [opinion, setOpinion] = useState("");
  const [template, setTemplate] = useState<delib.SentenceTemplate | null>(null);

  const reloadAll = useCallback(async () => {
    if (!Number.isFinite(dossierId)) return;
    try {
      const [l, s, c, n, v, cs, vt] = await Promise.all([
        delib.getLockStatus(dossierId),
        delib.listSessions(dossierId),
        delib.getConcordance(dossierId),
        delib.listNotes(dossierId),
        delib.listSentenceVersions(dossierId),
        delib.getConsentStatus(dossierId),
        delib.listVotes(dossierId),
      ]);
      setLock(l); setSessions(s); setConc(c); setNotes(n); setVersions(v);
      setConsent(cs.consent_donne); setVotes(vt);
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 403) setDenied(true);
    } finally {
      setLoading(false);
    }
  }, [dossierId]);
  useEffect(() => { void reloadAll(); }, [reloadAll]);

  // Modèle de sentence dans la langue choisie (fr/en/pt).
  useEffect(() => {
    delib.getSentenceTemplate(lang).then(setTemplate).catch(() => setTemplate(null));
  }, [lang]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast.success(ok); await reloadAll(); }
    catch (e) { toast.error(getApiErrorDetail(e) ?? "Action refusée."); }
  };

  if (denied) {
    return (
      <ArbiterShell title={t("title")}>
        <div className="p-6 text-sm" style={{ opacity: 0.8 }}>{t("access_denied")}</div>
      </ArbiterShell>
    );
  }

  return (
    <ArbiterShell title={t("title")}>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto w-full space-y-4">
        <div className="flex items-center gap-2">
          <Link href={`/cases/${dossierId}`} className="text-xs underline" style={{ color: GOLD }}>
            {t("back")} #{dossierId}
          </Link>
          <div className="ml-auto flex items-center gap-1">
            <Languages size={13} style={{ color: GOLD }} />
            <select className="rounded-md border px-1.5 py-0.5 text-xs" style={{ background: GRAPHITE, color: "white", borderColor: BORDER }}
              value={lang} onChange={(e) => setLang(e.target.value as Lang)}>
              <option value="fr">FR</option>
              <option value="en">EN</option>
              <option value="pt">PT</option>
            </select>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm" style={{ opacity: 0.7 }}>
            <Loader2 size={14} className="animate-spin" /> {t("loading")}
          </div>
        )}

        {consent === false && (
          <div className="rounded-md border p-3 space-y-2 text-[13px]" style={{ borderColor: "oklch(0.70 0.17 50)" }}>
            <div style={{ color: GOLD }}>{t("consent_title")}</div>
            <p style={{ opacity: 0.8 }}>{t("consent_text")}</p>
            <button type="button" className="rounded-md px-3 py-1 font-medium" style={{ background: GOLD, color: GRAPHITE }}
              onClick={() => run(() => delib.postConsent(dossierId), "OK")}>
              {t("accept")}
            </button>
          </div>
        )}

        <Section title={t("lock_section")}>
          {lock && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Lock size={14} style={{ color: lock.verrouille ? "oklch(0.6 0.2 25)" : GOLD }} />
              <strong>{lock.lock_status}</strong>
              {lock.escalade_requise && (
                <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px]" style={{ background: "oklch(0.6 0.2 25)", color: "white" }}>
                  <ShieldAlert size={12} /> {t("escalade")} (J-{lock.jours_restants_sentence})
                </span>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" className="rounded-md border px-2 py-1 text-sm" style={{ borderColor: BORDER, color: GOLD }}
              onClick={() => run(() => delib.openSession(dossierId), "OK")}>
              {t("open_session")}
            </button>
            {sessions[0] && (
              <button type="button" className="rounded-md border px-2 py-1 text-sm" style={{ borderColor: BORDER }}
                onClick={() => run(() => delib.closeInstruction(dossierId, sessions[0].id), "OK")}>
                {t("close_instruction")}
              </button>
            )}
          </div>
        </Section>

        <Section title={t("concordance_section")}>
          {conc && (
            <>
              <div className="h-2 rounded-full" style={{ background: BORDER }}>
                <div className="h-2 rounded-full" style={{ width: `${conc.progress_pct}%`, background: GOLD }} />
              </div>
              <div className="text-[12px]" style={{ opacity: 0.75 }}>
                {conc.traites}/{conc.total} {t("treated")} · {conc.ecartes} {t("discarded")} · {conc.progress_pct}%
                {conc.complete ? ` · ${t("ready_gen")}` : ""}
              </div>
              <ul className="space-y-1">
                {conc.lines.map((l) => (
                  <li key={l.id} className="flex flex-wrap items-center gap-2 text-[13px]">
                    {l.is_validated && <CheckCircle2 size={13} style={{ color: GOLD }} />}
                    <span>{l.chef_de_demande}</span>
                    {l.reason === "ECARTE" && <span className="text-[11px]" style={{ opacity: 0.6 }}>· {l.motif_ecart}</span>}
                    {!l.is_validated && l.reason !== "ECARTE" && (
                      <span className="ml-auto flex gap-1">
                        <button type="button" className="rounded border px-1.5 text-[11px]" style={{ borderColor: BORDER, color: GOLD }}
                          onClick={() => run(() => delib.validateConcordanceLine(dossierId, l.id, { is_validated: true }), "OK")}>
                          {t("validate")}
                        </button>
                        <button type="button" className="rounded border px-1.5 text-[11px]" style={{ borderColor: BORDER }}
                          onClick={() => {
                            const m = window.prompt(t("motif_prompt"));
                            if (m && m.trim()) run(() => delib.validateConcordanceLine(dossierId, l.id, { is_validated: false, reason: "ECARTE", motif_ecart: m }), "OK");
                          }}>
                          {t("discard")}
                        </button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="flex gap-2">
            <input className="flex-1 rounded-md border px-2 py-1 text-sm" style={{ background: GRAPHITE, color: "white", borderColor: BORDER }}
              placeholder={t("new_chef")} value={chef} onChange={(e) => setChef(e.target.value)} />
            <button type="button" className="rounded-md px-2 py-1 text-sm font-medium" style={{ background: GOLD, color: GRAPHITE }}
              disabled={chef.trim().length < 2}
              onClick={() => run(async () => { await delib.addConcordanceLine(dossierId, chef.trim()); setChef(""); }, "OK")}>
              {t("add")}
            </button>
          </div>
        </Section>

        <Section title={t("notes_section")}>
          <ul className="space-y-1 text-[13px]">
            {notes.map((n) => (
              <li key={n.id} className="rounded-md border p-2" style={{ borderColor: BORDER }}>{n.content}</li>
            ))}
          </ul>
          <textarea className="block w-full rounded-md border px-2 py-1 text-sm" rows={2}
            style={{ background: GRAPHITE, color: "white", borderColor: BORDER }}
            placeholder={t("new_note")} value={noteTxt} onChange={(e) => setNoteTxt(e.target.value)} />
          <button type="button" className="rounded-md px-2 py-1 text-sm font-medium" style={{ background: GOLD, color: GRAPHITE }}
            disabled={noteTxt.trim().length < 2}
            onClick={() => run(async () => { await delib.createNote(dossierId, noteTxt.trim()); setNoteTxt(""); }, "OK")}>
            {t("save_note")}
          </button>
        </Section>

        <Section title={t("votes_section")}>
          <ul className="space-y-1 text-[13px]">
            {votes.map((vo) => (
              <li key={vo.id} className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border px-2 py-0.5 text-[10px]" style={{ borderColor: BORDER, color: GOLD }}>{vo.vote_type}</span>
                {vo.opinion_text && <span style={{ opacity: 0.8 }}>{vo.opinion_text}</span>}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <select className="rounded-md border px-2 py-1 text-sm" style={{ background: GRAPHITE, color: "white", borderColor: BORDER }}
              value={voteType} onChange={(e) => setVoteType(e.target.value)}>
              <option value="MAJORITY">{t("vote_majority")}</option>
              <option value="CONCURRING">{t("vote_concurring")}</option>
              <option value="DISSENTING">{t("vote_dissenting")}</option>
            </select>
            <input className="flex-1 rounded-md border px-2 py-1 text-sm" style={{ background: GRAPHITE, color: "white", borderColor: BORDER }}
              placeholder={t("opinion_opt")} value={opinion} onChange={(e) => setOpinion(e.target.value)} />
            <button type="button" className="rounded-md px-2 py-1 text-sm font-medium" style={{ background: GOLD, color: GRAPHITE }}
              onClick={() => run(async () => { await delib.postVote(dossierId, voteType, opinion.trim() || undefined); setOpinion(""); }, "OK")}>
              {t("vote")}
            </button>
          </div>
        </Section>

        {/* Modèle de sentence multilingue (8 sections Art. 20 AUA) */}
        {template && (
          <Section title={t("template_section")}>
            <ol className="list-decimal pl-5 space-y-0.5 text-[13px]" style={{ opacity: 0.85 }}>
              {template.sections.map((s) => (
                <li key={s.section}>{s.titre}</li>
              ))}
            </ol>
          </Section>
        )}

        <Section title={t("versions_section")}>
          <ul className="space-y-2">
            {versions.map((v) => (
              <li key={v.id} className="rounded-md border p-2 text-[13px]" style={{ borderColor: BORDER }}>
                <div className="flex items-center gap-2">
                  <span className="font-mono">v{v.version_number}</span>
                  {v.is_final && <span className="text-[11px]" style={{ color: GOLD }}>{t("final")}</span>}
                  <button type="button" className="rounded border px-1.5 text-[11px]" style={{ borderColor: BORDER, color: GOLD }}
                    onClick={async () => {
                      try {
                        const exp = await delib.getSentenceExport(dossierId, v.id);
                        const blob = new Blob([JSON.stringify(exp, null, 2)], { type: "application/json" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url; a.download = `sentence-v${v.version_number}.json`; a.click();
                        URL.revokeObjectURL(url);
                      } catch (e) { toast.error(getApiErrorDetail(e) ?? "Export refusé."); }
                    }}>
                    {t("export")}
                  </button>
                  <span className="ml-auto font-mono text-[10px]" style={{ opacity: 0.6 }}>{v.content_hash.slice(0, 16)}…</span>
                </div>
                <SignaturePanel dossierId={dossierId} versionId={v.id} t={t} />
              </li>
            ))}
          </ul>
          <textarea className="block w-full rounded-md border px-2 py-1 text-sm" rows={2}
            style={{ background: GRAPHITE, color: "white", borderColor: BORDER }}
            placeholder={t("dispositif_ph")} value={dispositif} onChange={(e) => setDispositif(e.target.value)} />
          <button type="button" className="rounded-md px-2 py-1 text-sm font-medium" style={{ background: GOLD, color: GRAPHITE }}
            disabled={dispositif.trim().length < 2}
            onClick={() => run(async () => { await delib.createSentenceVersion(dossierId, { dispositif: dispositif.trim() }); setDispositif(""); }, "OK")}>
            {t("new_version")}
          </button>
        </Section>
      </div>
    </ArbiterShell>
  );
}
